import * as React from 'react';

import {CloseButton} from 'shells/button/close-button/close-button';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {ErrorState} from 'shells/error-state/error-state';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {Modal} from 'shells/modal/modal';

import type {
    GetActivityForModal_activity as ActivityForModal,
    ActivityUpdateInput,
    ActivityDeleteInput,
    GetSessionUser_loggedInUser as User,
    GetPotentialActivityEntities_activityPotentialEntities_relatedEntities as RelatedEntity,
    GetPotentialActivityEntities_activityPotentialEntities_participants as Participant,
    GetTeams_teams as Team,
} from 'nutshell-graphql-types';

import {analyticsClient} from '../../analytics-client';

// @ts-expect-error
import {Footer} from './footer';
import {refreshActivityState, getUnsavedChangesMessage, getStartingTabByVariant} from './helpers';
import {Calendar, Comments, InternalNotes, Recordings, Create, Transcript} from './tabs';
import type {PendingConfirmAction, UpdateLocation, ModalVariant} from './types';

import {Header, Sidebar} from '.';

import styles from './activity-modal.module.css';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    activity: ActivityForModal | null | undefined;
    isLoading: boolean;
    loadingError: string | null | undefined;
    updateError: string | null | undefined;
    currentUser: User | null | undefined;
    updateActivityForModal: (input: ActivityUpdateInput, location: UpdateLocation) => Promise<any>;
    deleteActivity: (arg1: ActivityDeleteInput) => Promise<any>;
    isUpdatingActivity: boolean;
    potentialRelatedEntities?: RelatedEntity[];
    potentialParticipants?: Participant[];
    handlePollForSummaryAndTranscript: () => Promise<any>;
    teams: Team[] | null | undefined;
    lastUpdateLocation: UpdateLocation | null | undefined;
    shouldPollForSync: boolean;
    setShouldPollForSync: (arg1: boolean) => void;
    variant: ModalVariant;
};

export function ActivityModal(props: Props) {
    const [activeTabId, setActiveTabId] = React.useState<string>(
        getStartingTabByVariant(props.variant)
    );
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState<boolean>(false);
    const [unsavedConfirmationOpen, setUnsavedConfirmationOpen] = React.useState<boolean>(false);
    const [pendingConfirmAction, setPendingConfirmAction] =
        React.useState<PendingConfirmAction>(null);

    const hasChangedRef = React.useRef<boolean>(false);

    const handleUpdateActivity = React.useCallback(
        (
            input: ActivityUpdateInput,
            location: UpdateLocation,
            shouldSetHasChanged = true
        ): Promise<any> => {
            return props.updateActivityForModal(input, location).then((result) => {
                if (shouldSetHasChanged) {
                    hasChangedRef.current = true;
                }

                // If the an activity on a lead page has its related lead
                // removed, we need to refetch for it to be removed from the
                // lead page
                if (input.relatedLeadIds) {
                    window.dispatchEvent(new Event('activityRelatedLeadsUpdated'));
                }

                return result;
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.updateActivityForModal]
    );

    const handleChangeTab = (tabId: string) => {
        if (hasUnsavedChanges) {
            setUnsavedConfirmationOpen(true);
            setPendingConfirmAction({type: 'changeTab', tabId});
        } else {
            setActiveTabId(tabId);
            analyticsClient.track(analyticsClient.EVENTS.TRACK_ACTIVITY_MODAL_TAB_VIEWED, {
                tabId: tabId,
            });
        }
    };

    const closeModal = () => {
        props.onClose();
        refreshActivityState(hasChangedRef.current, props.activity ? props.activity.id : undefined);
    };

    const handleClose = (): void => {
        if (hasUnsavedChanges) {
            setPendingConfirmAction({type: 'close'});
            setUnsavedConfirmationOpen(true);
        } else {
            closeModal();
        }
    };

    const handleBeforeClose = (): boolean => {
        if (hasUnsavedChanges) {
            setPendingConfirmAction({type: 'close'});
            setUnsavedConfirmationOpen(true);

            return false;
        } else {
            closeModal();
        }

        return true;
    };

    const handleConfirmUnsavedChanges = () => {
        if (!pendingConfirmAction) {
            setUnsavedConfirmationOpen(false);

            return;
        }

        if (pendingConfirmAction.type === 'close') {
            closeModal();
        } else if (pendingConfirmAction.type === 'changeTab') {
            setActiveTabId(pendingConfirmAction.tabId);
        }

        setPendingConfirmAction(null);
        setUnsavedConfirmationOpen(false);
    };

    // When switching between modal states, we need to move from the schedule or
    // log tab to the internal notes tab
    React.useEffect(() => {
        if (props.activity && (activeTabId === 'schedule' || activeTabId === 'log')) {
            setActiveTabId('internal-notes');
        } else if (!props.activity) {
            setActiveTabId(getStartingTabByVariant(props.variant));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.activity, props.variant]);

    const isReadOnly = props.activity ? !props.activity.isEditable : false;

    return (
        <Modal
            isOpen={props.isOpen}
            onBeforeClose={handleBeforeClose}
            noPadding={true}
            closeButton={false}
            visibleOverflow={true}
        >
            <div className={styles['close-button-container']}>
                <CloseButton onClick={handleClose} size={16} />
            </div>
            {props.isLoading && <LoadingPage />}
            {props.loadingError && !props.isLoading && (
                <ErrorState errorMessage={props.loadingError} size='big' />
            )}
            {!props.isLoading && !props.loadingError && (
                <div className='flex flex-dir-col full-width full-height'>
                    <Header
                        activity={props.activity}
                        handleUpdateActivity={handleUpdateActivity}
                        handleDeleteActivity={props.deleteActivity}
                        setHasUnsavedChanges={setHasUnsavedChanges}
                        isReadOnly={isReadOnly}
                    />
                    <div className='flex full-width full-height overflow-auto'>
                        <Sidebar
                            activeTab={activeTabId}
                            setActiveTab={handleChangeTab}
                            activity={props.activity}
                            variant={props.variant}
                        />
                        {(activeTabId === 'schedule' || activeTabId === 'log') && (
                            <Create
                                setHasUnsavedChanges={setHasUnsavedChanges}
                                logOnCreate={activeTabId === 'log'}
                                teams={props.teams}
                            />
                        )}
                        {props.activity && props.currentUser && (
                            <>
                                {activeTabId === 'internal-notes' && (
                                    <InternalNotes
                                        activity={props.activity}
                                        potentialRelatedEntities={props.potentialRelatedEntities}
                                        handleUpdateActivity={handleUpdateActivity}
                                        handlePollForSummaryAndTranscript={
                                            props.handlePollForSummaryAndTranscript
                                        }
                                        teams={props.teams}
                                    />
                                )}
                                {activeTabId === 'calendar' && (
                                    <Calendar
                                        activity={props.activity}
                                        onSave={handleUpdateActivity}
                                        isSaving={
                                            props.isUpdatingActivity &&
                                            props.lastUpdateLocation === 'calendar'
                                        }
                                        updateError={
                                            props.lastUpdateLocation === 'calendar' &&
                                            props.updateError
                                                ? props.updateError
                                                : null
                                        }
                                        potentialParticipants={props.potentialParticipants}
                                        setHasUnsavedChanges={setHasUnsavedChanges}
                                        isReadOnly={isReadOnly}
                                    />
                                )}
                                {activeTabId === 'transcript' && (
                                    <Transcript
                                        activity={props.activity}
                                        isAdmin={
                                            props.currentUser && props.currentUser.isAdministrator
                                        }
                                    />
                                )}
                                {activeTabId === 'recordings' && (
                                    <Recordings activity={props.activity} />
                                )}
                                {activeTabId === 'comments' && (
                                    <Comments
                                        activity={props.activity}
                                        currentUser={props.currentUser}
                                    />
                                )}
                            </>
                        )}
                    </div>
                    <Footer
                        activity={props.activity}
                        handleUpdateActivity={handleUpdateActivity}
                        onCancel={handleClose}
                        setHasUnsavedChanges={setHasUnsavedChanges}
                        updateError={
                            (props.lastUpdateLocation === 'footer' ||
                                props.lastUpdateLocation === 'header' ||
                                props.lastUpdateLocation === 'internal-notes') &&
                            props.updateError
                                ? props.updateError
                                : null
                        }
                        shouldPollForSync={props.shouldPollForSync}
                        setShouldPollForSync={props.setShouldPollForSync}
                    />
                </div>
            )}
            {unsavedConfirmationOpen && (
                <ConfirmationDialog
                    title='Unsaved changes'
                    message={getUnsavedChangesMessage(pendingConfirmAction)}
                    onCancel={() => setUnsavedConfirmationOpen(false)}
                    onConfirm={handleConfirmUnsavedChanges}
                />
            )}
        </Modal>
    );
}
